import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import { Link } from "react-router-dom";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

const recentWebsitesSample = [
  {
    url: "www.workspace.google.com",
    project: "SkyGrid",
    duration: "01:40",
  },
  {
    url: "www.acadecraft.com",
    project: "Project Quantum",
    duration: "00:40",
  },
  {
    url: "www.chromewebstore.google.com",
    project: "Digital Marketing",
    duration: "02:30",
  },
  {
    url: "www.account.hubstaff.com",
    project: "Project Quantum",
    duration: "03:15",
  },
  {
    url: "www.hotspot.bhaifi.com",
    project: "SkyGrid",
    duration: "03:15",
  },
];

// Favicon generator fallback
const getFavicon = (url) => {
  const domain = url.replace("www.", "").split("/")[0];
  return `https://www.google.com/s2/favicons?sz=64&domain_url=https://${domain}`;
};

// Skeleton loader with numbering
const TableRowsLoader = ({ rowsNum }) => {
  return [...Array(rowsNum)].map((_, index) => (
    <TableRow key={index}>
      {/* Numbering Skeleton */}
      <TableCell>
        <Skeleton animation="wave" variant="text" width={20} />
      </TableCell>

      {/* Avatar + Text Skeleton */}
      <TableCell>
        <Box display="flex" alignItems="center" gap={2}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={180} height={20} />
            <Skeleton
              variant="text"
              width={120}
              height={14}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
      </TableCell>

      {/* Duration Skeleton */}
      <TableCell align="right">
        <Skeleton variant="text" width={60} />
      </TableCell>
    </TableRow>
  ));
};

const RecentWebsiteActivity = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setData(recentWebsitesSample);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Paper className="card-body p-20 bg-base border rounded-3 h-100" sx={{ boxShadow: 'none' }}>
      <Box display="flex" justifyContent="space-between" sx={{ flexWrap: { sm: 'wrap' } }} className=" pb-16 mb-0 border-bottom">
        <h6 className=" fw-semibold mb-0" style={{ fontSize: '17px' }}>Recent Website Activity  <Tooltip title="Recent Website Activity" arrow placement="top">
          <button className="tooltipBtn ms-2 border-0 bg-transparent p-0">
            <InfoOutlineIcon fontSize="small" />
          </button>
        </Tooltip></h6>
        <Link to="/urls"
          style={{ cursor: "pointer", fontSize: '14px', color: '#004DFF', }}
        >
          View report
        </Link>
      </Box>

      <TableContainer>
        <Table className="tablegridall">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Website</TableCell>
              <TableCell align="right">Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: "none", // 🔥 remove table body cell borders
              },
            }}
          >
            {!data ? (
              <TableRowsLoader rowsNum={5} />
            ) : (
              data.map((site, index) => {
                const initials = site.url
                  .replace("www.", "")
                  .split(".")[0]
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <TableRow key={index}>
                    {/* Numbering */}
                    <TableCell>{index + 1}</TableCell>

                    {/* Favicon + URL + Project */}
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={getFavicon(site.url)}
                          alt={site.url}
                          onError={(e) => (e.target.style.display = "none")}
                          sx={{ width: 40, height: 40, fontSize: '14px', }}
                        >
                          {initials}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1F36', }}>{site.url}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {site.project}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Duration */}
                    <TableCell align="right">
                      <Typography variant="body2">{site.duration}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RecentWebsiteActivity;
